import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import {
  getAssessment,
  getAssessmentResultList,
  getAssessmentResultsOverview,
} from '../../../../utils/api_service/assessment_api';
import { CandidateStatusEnum } from '../../../../utils/helpers/candidateStatus';
import { RootState } from '../../../../utils/redux_store/store';
import {
  Assessment,
  AssessmentUser,
  CandidateStatus,
  IAssessmentResultOverview,
  ResultRequestResponse,
  UserAssessmentResponse,
} from '../../../../utils/types/assessment_types';
import { SelectOptions } from '../../../../utils/types/react_select_types';
import { Tabs } from '../tabs/tabs';
import { AssessmentEmptyList } from './assessment_empty_list/assessment_empty_list';
import { AssessmentResults } from './assessment_result_list/assessment_result_list';
import { AssessmentSummary } from './assessment_summary/assessment_summary';
import { AssessmentUsers } from './assessment_users/assessment_users';
import { OrgAssessmentUsers } from './OrgAssessmentUsers';
import { PartnerAssessmentUsers } from './PartnerAssessmentUsers';
import { PreScreenQuestions } from './PreScreenQuestions';
import { Questions } from './Questions';
import { Settings } from './settings/settings';

const PRACTICE: string = 'practice';

export const ViewAssessment = () => {
  const { id, tab } = useParams<{ id: string; tab: string }>();
  const [assessmentInfo, setAssessmentInfo] = useState<Assessment>();
  const [activeTab, setActiveTab] = useState<number>(parseInt(tab) || 0);
  const [assessmentUserListResponse, setAssessmentUserListResponse] =
    useState<ResultRequestResponse>();
  const [overviewResultResponse, setOverviewResultResponse] =
    useState<IAssessmentResultOverview>();
  const [highestResultScore, setHighestResultScore] = useState<number>();
  const skills = useSelector((state: RootState) => state.skills);
  const levels = useSelector((state: RootState) => state.skillLevels);
  const jobTitles = useSelector((state: RootState) => state.jobTitles);
  const subSkills = useSelector((state: RootState) => state.subSkills);
  const [skillList, setSkillList] = useState<SelectOptions[]>(
    skills.skillsList,
  );
  const [loaded, setLoaded] = useState<boolean>(false);
  const practiceTab = [
    'List of Questions',
    'Permissions and Settings',
    'Results',
  ];
  let tabTitles: string[] = [];

  // Dynamic tab generation based on available data
  // Dynamic tab generation based on available data
  const getDynamicTabs = () => {
    const baseTabs = [
      'Summary',
      'Candidate',
      'Pre Screen Questions',
      'List of Questions',
      'Permissions and Settings',
      'Results',
    ];

    if (assessmentUserListResponse?.assessmentUserList) {
      const userList = assessmentUserListResponse.assessmentUserList;

      // Check if we have users with WAITLISTED status
      const hasWaitlisted = userList.some(
        (user) => user.candidateStatus === CandidateStatusEnum.WAITLISTED,
      );
      if (hasWaitlisted) {
        baseTabs.push(CandidateStatusEnum.WAITLISTED);
      }

      // Check if we have users with SHORTLISTED status
      const hasShortlisted = userList.some(
        (user) => user.candidateStatus === CandidateStatusEnum.SHORTLISTED,
      );
      if (hasShortlisted) {
        baseTabs.push(CandidateStatusEnum.SHORTLISTED);
      }

      // Check if we have users with UNSUCCESSFUL status
      const hasUnsuccessful = userList.some(
        (user) => user.candidateStatus === CandidateStatusEnum.UNSUCCESSFUL,
      );
      if (hasUnsuccessful) {
        baseTabs.push(CandidateStatusEnum.UNSUCCESSFUL);
      }
    }

    return baseTabs;
  };

  // Helper function to get current tab title
  const getCurrentTabTitle = () => {
    return tabTitles[activeTab];
  };

  if (assessmentInfo?.assessmentType === 'candidate') {
    tabTitles = getDynamicTabs();
  } else if (assessmentInfo?.assessmentType === 'employee') {
    tabTitles = [
      'Summary',
      'Team Members',
      'List of Questions',
      'Permissions and Settings',
      'Results',
    ];
  } else {
    tabTitles = [
      'Summary',
      'Partners',
      'List of Questions',
      'Permissions and Settings',
      'Results',
    ];
  }

  useEffect(() => {
    getAssessment(id).then((response) => {
      setAssessmentInfo(response);
    });
  }, [id, activeTab]);

  // Adjust active tab if current tab is no longer available (for dynamic tabs)
  useEffect(() => {
    if (
      assessmentInfo?.assessmentType === 'candidate' &&
      tabTitles.length > 0 &&
      activeTab >= tabTitles.length
    ) {
      setActiveTab(0);
    }
  }, [tabTitles.length, activeTab, assessmentInfo?.assessmentType]);

  useEffect(() => {
    if (assessmentInfo?.id) {
      getAssessmentResultList(assessmentInfo.id).then((response) => {
        if (!response.error && response.assessmentUserList.length !== 0) {
          setAssessmentUserListResponse(response);
          const validScores = response.assessmentUserList
            .filter(
              (result: UserAssessmentResponse) =>
                result.maxScore !== undefined && result.maxScore > 0,
            )
            .map((result: UserAssessmentResponse) => {
              const score = (result.score / (result.maxScore as number)) * 100;
              return isFinite(score) ? Math.min(score, 100) : 0;
            });

          if (validScores.length > 0) {
            setHighestResultScore(Math.round(Math.max(...validScores)));
          } else {
            setHighestResultScore(0);
          }
          setLoaded(true);
        }
      });
      getAssessmentResultsOverview(assessmentInfo.id).then((response) => {
        if (
          (!response.error && response.skills.length !== 0) ||
          response.behaviorSkills.length !== 0
        ) {
          setOverviewResultResponse(response);
        }
      });
    }
  }, [assessmentInfo?.id]);

  useEffect(() => {
    setSkillList(skills.skillsList);
  }, [skills.skillsList]);

  const updateAssessmentUserList = (
    selectedRows: AssessmentUser[],
    candidateStatus: CandidateStatus,
  ) => {
    if (assessmentUserListResponse) {
      const updatedAssessmentUserListResponse = {
        ...assessmentUserListResponse,
      };
      selectedRows.forEach((selectedRow) => {
        const userIndex =
          updatedAssessmentUserListResponse.assessmentUserList.findIndex(
            (user) => user.id === selectedRow.assessmentUserId,
          );
        if (userIndex !== -1) {
          updatedAssessmentUserListResponse.assessmentUserList[
            userIndex
          ].candidateStatus = candidateStatus;
        }
      });
      setAssessmentUserListResponse(updatedAssessmentUserListResponse);
    }
  };

  const renderTabContent = () => {
    if (assessmentInfo) {
      const currentTab = getCurrentTabTitle();

      if (currentTab === 'Summary') {
        if (assessmentInfo.assessmentType !== PRACTICE) {
          return <AssessmentSummary assessment={assessmentInfo} />;
        } else {
          return (
            <div className='my-5'>
              <Questions
                  assessmentId={assessmentInfo.id}
                  skillList={skillList}
                  levelList={levels.levelList}
                  jobTitleList={jobTitles.jobTitlesList}
                  subSkillList={subSkills.subSkillsList}
                  setSkillList={setSkillList}
              />
            </div>
          );
        }
      } else if (
        currentTab === 'Candidate' ||
        currentTab === 'Team Members' ||
        currentTab === 'Partners'
      ) {
        if (
          assessmentInfo.assessmentType === 'employee' &&
          assessmentInfo.queryFilter
        ) {
          return (
            <OrgAssessmentUsers
                assessmentId={assessmentInfo.id}
                query={assessmentInfo.queryFilter}
            />
          );
        } else if (assessmentInfo.assessmentType === 'candidate') {
          return <AssessmentUsers assessmentId={assessmentInfo.id} />;
        } else if (assessmentInfo.assessmentType === 'partner') {
          return (
            <PartnerAssessmentUsers
                assessmentId={assessmentInfo.id}
                query={assessmentInfo.queryFilter}
            />
          );
        } else {
          return <Settings assessmentType={assessmentInfo.assessmentType} setActiveTab={setActiveTab as any} />;
        }
      } else if (currentTab === 'Pre Screen Questions') {
        if (assessmentInfo.assessmentType === 'candidate') {
          return (
            <div className='my-5'>
              <PreScreenQuestions
                  assessmentInfo={assessmentInfo}
                  setAssessmentInfo={setAssessmentInfo}
              />
            </div>
          );
        } else if (
          assessmentInfo.assessmentType === 'employee' ||
          assessmentInfo.assessmentType === 'partner'
        ) {
          return (
            <div className='my-5'>
              <Questions
                  assessmentId={assessmentInfo.id}
                  skillList={skillList}
                  levelList={levels.levelList}
                  jobTitleList={jobTitles.jobTitlesList}
                  subSkillList={subSkills.subSkillsList}
                  setSkillList={setSkillList}
              />
            </div>
          );
        } else {
          if (loaded) {
            return (
              <AssessmentResults
                  preScreenQuestions={
                    assessmentInfo.preScreen ? assessmentInfo.preScreen : []
                  }
                  assessmentId={assessmentInfo.id}
                  userResults={
                    assessmentUserListResponse?.assessmentUserList || []
                  }
                  completionRatio={
                    assessmentUserListResponse?.completionRatio || ''
                  }
                  highestScore={highestResultScore || 0}
                  behaviorSkills={overviewResultResponse?.behaviorSkills || []}
                  skills={overviewResultResponse?.skills || []}
                  countCompleted={assessmentUserListResponse?.countCompleted || 0}
                  countTotal={assessmentUserListResponse?.countTotal || 0}
              />
            );
          }
        }
      } else if (currentTab === 'List of Questions') {
        if (assessmentInfo?.assessmentType === 'candidate') {
          return (
            <div className='my-5'>
              <Questions
                  assessmentId={assessmentInfo.id}
                  skillList={skillList}
                  levelList={levels.levelList}
                  jobTitleList={jobTitles.jobTitlesList}
                  subSkillList={subSkills.subSkillsList}
                  setSkillList={setSkillList}
              />
            </div>
          );
        } else {
          return <Settings assessmentType={assessmentInfo.assessmentType} setActiveTab={setActiveTab as any} />;
        }
      } else if (currentTab === 'Permissions and Settings') {
        if (assessmentInfo?.assessmentType === 'candidate') {
          return <Settings assessmentType={assessmentInfo.assessmentType} setActiveTab={setActiveTab as any} />;
        } else {
          return (
            <AssessmentResults
                preScreenQuestions={
                  assessmentInfo.preScreen ? assessmentInfo.preScreen : []
                }
                assessmentId={assessmentInfo.id}
                userResults={assessmentUserListResponse?.assessmentUserList || []}
                completionRatio={
                  assessmentUserListResponse?.completionRatio || ''
                }
                highestScore={highestResultScore || 0}
                behaviorSkills={overviewResultResponse?.behaviorSkills || []}
                skills={overviewResultResponse?.skills || []}
                countCompleted={assessmentUserListResponse?.countCompleted || 0}
                countTotal={assessmentUserListResponse?.countTotal || 0}
            />
          );
        }
      } else if (currentTab === 'Results') {
        if (assessmentInfo?.assessmentType === 'candidate') {
          if (loaded) {
            return (
              <AssessmentResults
                  preScreenQuestions={
                    assessmentInfo.preScreen ? assessmentInfo.preScreen : []
                  }
                  assessmentId={assessmentInfo.id}
                  userResults={
                    assessmentUserListResponse?.assessmentUserList || []
                  }
                  completionRatio={
                    assessmentUserListResponse?.completionRatio || ''
                  }
                  highestScore={highestResultScore || 0}
                  // candidateStatus is intentionally undefined for Results tab
                  updateAssessmentUserList={updateAssessmentUserList}
                  behaviorSkills={overviewResultResponse?.behaviorSkills || []}
                  skills={overviewResultResponse?.skills || []}
                  countCompleted={assessmentUserListResponse?.countCompleted || 0}
                  countTotal={assessmentUserListResponse?.countTotal || 0}
              />
            );
          } else {
            return <AssessmentEmptyList />;
          }
        } else {
          return <AssessmentEmptyList />;
        }
      } else if (currentTab === CandidateStatusEnum.WAITLISTED) {
        if (loaded) {
          return (
            <AssessmentResults
                preScreenQuestions={
                  assessmentInfo.preScreen ? assessmentInfo.preScreen : []
                }
                assessmentId={assessmentInfo.id}
                userResults={assessmentUserListResponse?.assessmentUserList || []}
                completionRatio={
                  assessmentUserListResponse?.completionRatio || ''
                }
                highestScore={highestResultScore || 0}
                candidateStatus={CandidateStatusEnum.WAITLISTED}
                updateAssessmentUserList={updateAssessmentUserList}
                behaviorSkills={overviewResultResponse?.behaviorSkills || []}
                skills={overviewResultResponse?.skills || []}
                countCompleted={assessmentUserListResponse?.countCompleted || 0}
                countTotal={assessmentUserListResponse?.countTotal || 0}
            />
          );
        } else {
          return <AssessmentEmptyList />;
        }
      } else if (currentTab === CandidateStatusEnum.SHORTLISTED) {
        if (loaded) {
          return (
            <AssessmentResults
                preScreenQuestions={
                  assessmentInfo.preScreen ? assessmentInfo.preScreen : []
                }
                assessmentId={assessmentInfo.id}
                userResults={assessmentUserListResponse?.assessmentUserList || []}
                completionRatio={
                  assessmentUserListResponse?.completionRatio || ''
                }
                highestScore={highestResultScore || 0}
                candidateStatus={CandidateStatusEnum.SHORTLISTED}
                updateAssessmentUserList={updateAssessmentUserList}
                behaviorSkills={overviewResultResponse?.behaviorSkills || []}
                skills={overviewResultResponse?.skills || []}
                countCompleted={assessmentUserListResponse?.countCompleted || 0}
                countTotal={assessmentUserListResponse?.countTotal || 0}
            />
          );
        } else {
          return <AssessmentEmptyList />;
        }
      } else if (currentTab === CandidateStatusEnum.UNSUCCESSFUL) {
        if (loaded) {
          return (
            <AssessmentResults
                preScreenQuestions={
                  assessmentInfo.preScreen ? assessmentInfo.preScreen : []
                }
                assessmentId={assessmentInfo.id}
                userResults={assessmentUserListResponse?.assessmentUserList || []}
                completionRatio={
                  assessmentUserListResponse?.completionRatio || ''
                }
                highestScore={highestResultScore || 0}
                candidateStatus={CandidateStatusEnum.UNSUCCESSFUL}
                updateAssessmentUserList={updateAssessmentUserList}
                behaviorSkills={overviewResultResponse?.behaviorSkills || []}
                skills={overviewResultResponse?.skills || []}
                countCompleted={assessmentUserListResponse?.countCompleted || 0}
                countTotal={assessmentUserListResponse?.countTotal || 0}
            />
          );
        } else {
          return <AssessmentEmptyList />;
        }
      }
    }
  };

  return (
    <div className='w-full !bg-white px-4'>
      <nav
          className='flex w-full justify-between items-center border-b py-2 text-xs'
          aria-label='Breadcrumb'
      >
        <ol className='inline-flex items-center space-x-1 md:space-x-2'>
          <li>
            <Link to={'/assessment-list'}>Back to Assessment List</Link>
          </li>
          <li>
            <span className='text-gray-400'>/</span>
          </li>
          { assessmentInfo && (
            <li>
              <Link
                  to={'#'}
                  className='text-primary-dark font-semibold hover:underline'
              >
                { assessmentInfo.assessmentTitle }
              </Link>
            </li>
          ) }
        </ol>
      </nav>

      { assessmentInfo && (
        <>
          <Tabs
              tabTitles={
                assessmentInfo.assessmentType === PRACTICE
                  ? practiceTab
                  : tabTitles
              }
              activeTab={activeTab}
              setActiveTab={setActiveTab}
          />
          { renderTabContent() }
        </>
      ) }
    </div>
  );
};
